import React from "react";
import { FaWhatsapp } from "react-icons/fa";
import { ArrowDown, Clock, Mic, Paperclip, Check, CheckCheck } from "lucide-react";
import { useLang } from "../lib/LangContext";
import { waLink, heroImages } from "../lib/mock";
import { HERO } from "../constants/testIds";

export const Hero = () => {
  const { t } = useLang();
  return (
    <section
      id="top"
      className="relative overflow-hidden"
    >
      {/* Decorative grain / texture */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #0A3B24 1px, transparent 0)",
          backgroundSize: "28px 28px",
        }}
      />

      <div className="relative px-6 md:px-12 lg:px-20 max-w-[1400px] mx-auto pt-14 pb-20 md:pt-24 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left – Text */}
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-lokam-tangerine/30 bg-lokam-tangerine/5 px-3 py-1.5 text-xs uppercase tracking-[0.18em] font-bold text-lokam-tangerine">
              <span className="h-1.5 w-1.5 rounded-full bg-lokam-tangerine animate-pulse" />
              {t.hero.eyebrow}
            </div>

            <h1 className="mt-6 font-display font-bold text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] tracking-tight text-lokam-forest text-balance">
              {t.hero.title_part1}{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-lokam-tangerine">
                  {t.hero.title_highlight}
                </span>
                <svg
                  aria-hidden
                  viewBox="0 0 300 14"
                  className="absolute left-0 -bottom-1 w-full h-[10px] text-lokam-tangerine/40"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M2 8 C 60 2, 140 14, 298 6"
                    stroke="currentColor"
                    strokeWidth="4"
                    strokeLinecap="round"
                    fill="none"
                  />
                </svg>
              </span>
            </h1>

            <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-lokam-ink-soft">
              {t.hero.subtitle}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={HERO.whatsappBtn}
                className="inline-flex items-center gap-3 rounded-full bg-lokam-whatsapp hover:bg-lokam-whatsapp-dark text-white px-6 py-4 text-base md:text-lg font-semibold shadow-[0_8px_28px_rgba(37,211,102,0.35)] transition-all hover:-translate-y-0.5"
              >
                <FaWhatsapp className="h-6 w-6" />
                {t.hero.cta_primary}
              </a>

              <a
                href="#how-it-works"
                data-testid={HERO.secondaryBtn}
                className="inline-flex items-center gap-2 rounded-full border border-lokam-forest/20 text-lokam-forest hover:bg-lokam-forest hover:text-lokam-cream px-5 py-4 text-base font-semibold transition-colors"
              >
                {t.hero.cta_secondary}
                <ArrowDown className="h-4 w-4" />
              </a>
            </div>

            <p className="mt-6 text-sm text-lokam-ink-soft flex items-center gap-2">
              <Clock className="h-4 w-4 text-lokam-tangerine" />
              {t.hero.trust}
            </p>
          </div>

          {/* Right – WhatsApp mock chat */}
          <div className="lg:col-span-5">
            <div className="relative max-w-md mx-auto lg:ml-auto float-anim">
              {/* Phone shell */}
              <div className="relative rounded-[2.5rem] bg-lokam-forest p-3 shadow-[0_30px_60px_-20px_rgba(10,59,36,0.45)]">
                {/* Screen */}
                <div className="rounded-[2rem] overflow-hidden bg-[#ECE5DD]">
                  {/* WA Top bar */}
                  <div className="bg-[#075E54] text-white px-4 py-3 flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-white overflow-hidden flex items-center justify-center flex-shrink-0">
                      <img
                        src="https://customer-assets.emergentagent.com/job_medicine-dial/artifacts/zpmtbx62_Lokam-Secondary2%403x-100.jpg"
                        alt="Lokam"
                        className="h-9 w-9 object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold leading-tight">Lokam Health</div>
                      <div className="text-[11px] opacity-80">online</div>
                    </div>
                    <FaWhatsapp className="h-5 w-5" />
                  </div>

                  {/* Chat area */}
                  <div className="px-3 py-4 space-y-2 min-h-[420px] bg-[#ECE5DD]">
                    {/* Outgoing */}
                    <div className="flex justify-end">
                      <div className="max-w-[80%] bg-[#DCF8C6] rounded-2xl rounded-tr-md px-3.5 py-2 shadow-sm">
                        <div className="text-[13.5px] text-[#1A1814] leading-snug">
                          {t.chat.bubble1}
                        </div>
                        <div className="text-[10px] text-[#5C5954] mt-1 text-right flex items-center justify-end gap-1">
                          10:24
                          <CheckCheck className="h-3 w-3 text-[#34B7F1]" />
                        </div>
                      </div>
                    </div>

                    {/* Outgoing with prescription image */}
                    <div className="flex justify-end">
                      <div className="max-w-[80%] bg-[#DCF8C6] rounded-2xl rounded-tr-md p-1.5 shadow-sm">
                        <div className="rounded-xl overflow-hidden relative">
                          <img
                            src={heroImages.pills}
                            alt="prescription"
                            className="block w-full h-44 object-cover object-top"
                          />
                          <span className="absolute bottom-1.5 left-1.5 text-[10px] bg-black/55 text-white rounded-md px-1.5 py-0.5 flex items-center gap-1">
                            <Paperclip className="h-2.5 w-2.5" /> prescription.jpg
                          </span>
                        </div>
                        <div className="px-2 pt-1.5 pb-0.5">
                          <div className="text-[13px] text-[#1A1814]">
                            {t.chat.bubble2}
                          </div>
                          <div className="text-[10px] text-[#5C5954] text-right flex items-center justify-end gap-1">
                            10:24
                            <CheckCheck className="h-3 w-3 text-[#34B7F1]" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Incoming – Lokam reply */}
                    <div className="flex justify-start">
                      <div className="max-w-[85%] bg-white rounded-2xl rounded-tl-md px-3.5 py-2 shadow-sm">
                        <div className="text-[11px] text-lokam-tangerine font-bold uppercase tracking-wider mb-1">
                          Lokam
                        </div>
                        <div className="text-[13.5px] text-[#1A1814] leading-snug">
                          {t.chat.bubble3}
                        </div>
                        <div className="text-[10px] text-[#5C5954] mt-1 text-right">
                          10:24
                        </div>
                      </div>
                    </div>

                    {/* Outgoing reply */}
                    <div className="flex justify-end">
                      <div className="bg-[#DCF8C6] rounded-2xl rounded-tr-md px-3.5 py-2 shadow-sm">
                        <div className="text-[13.5px] text-[#1A1814]">
                          {t.chat.bubble4}
                        </div>
                        <div className="text-[10px] text-[#5C5954] mt-0.5 text-right flex items-center justify-end gap-1">
                          10:25
                          <CheckCheck className="h-3 w-3 text-[#34B7F1]" />
                        </div>
                      </div>
                    </div>

                    {/* System / delivery card */}
                    <div className="flex justify-center pt-1">
                      <div className="bg-lokam-forest text-lokam-cream rounded-full px-3 py-1 text-[11px] font-semibold flex items-center gap-1.5">
                        <Clock className="h-3 w-3 text-lokam-tangerine" />
                        {t.chat.delivery}
                      </div>
                    </div>
                  </div>

                  {/* WA input */}
                  <div className="bg-[#F0F0F0] px-3 py-2 flex items-center gap-2">
                    <div className="flex-1 bg-white rounded-full px-3 py-2 text-xs text-[#999]">
                      Type a message
                    </div>
                    <button
                      aria-label="voice"
                      className="h-9 w-9 rounded-full bg-lokam-whatsapp text-white flex items-center justify-center"
                    >
                      <Mic className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Floating bubble accent */}
              <div className="absolute -left-6 -top-6 rounded-2xl bg-lokam-tangerine text-lokam-cream px-3 py-2 text-xs font-bold uppercase tracking-wider shadow-xl rotate-[-6deg]">
                60 min delivery
              </div>
              <div className="absolute -right-4 bottom-12 rounded-2xl bg-white border border-lokam-border px-3 py-2 text-xs font-semibold text-lokam-forest shadow-xl rotate-[4deg] flex items-center gap-1.5">
                <Check className="h-3.5 w-3.5 text-lokam-whatsapp" />
                Auto-confirmed
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
